import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { pathsApi } from '../services/api';
import { ArrowRight, Users, Clock, Loader2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const LearningPaths = () => {
  const navigate = useNavigate();
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPaths = async () => {
      try {
        setLoading(true);
        const data = await pathsApi.getAll();
        setPaths(data);
      } catch (err) {
        console.error('Error fetching paths:', err);
        setError('Failed to load learning paths');
      } finally {
        setLoading(false);
      }
    };
    fetchPaths();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-16">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-3">Learning Paths</h1>
          <p className="text-gray-400 text-lg">Structured courses to guide your cybersecurity journey from beginner to expert</p>
        </div>

        {error && (
          <div className="text-center py-12">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        <div className="grid gap-6">
          {paths.map((path) => (
            <Card 
              key={path.id}
              className="bg-[#1A1A1A] border-gray-800 hover:border-[#00FF00]/50 transition-all duration-300 hover:transform hover:scale-[1.01]"
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div 
                        className="w-1 h-12 rounded-full"
                        style={{ backgroundColor: path.color || '#00FF00' }}
                      />
                      <div>
                        <CardTitle className="text-2xl text-white mb-1">{path.title}</CardTitle>
                        <CardDescription className="text-gray-400">{path.description}</CardDescription>
                      </div>
                    </div>
                  </div>
                  <Badge 
                    className="ml-4" 
                    style={{ 
                      backgroundColor: `${path.color || '#00FF00'}20`, 
                      color: path.color || '#00FF00', 
                      borderColor: path.color || '#00FF00' 
                    }}
                  >
                    {path.level}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gray-800">
                      <Clock className="w-5 h-5 text-[#00BFFF]" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Duration</div>
                      <div className="font-semibold text-white">{path.duration || 'Self-paced'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gray-800">
                      <Users className="w-5 h-5 text-[#FF9500]" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Enrolled</div>
                      <div className="font-semibold text-white">{(path.enrolled || 0).toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gray-800">
                      <ArrowRight className="w-5 h-5 text-[#00FF00]" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Modules</div>
                      <div className="font-semibold text-white">{path.modules?.length || 0} modules</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Your Progress</span>
                    <span className="text-white font-medium">0%</span>
                  </div>
                  <Progress value={0} className="h-2" />
                </div>

                <Button 
                  onClick={() => navigate('/rooms')}
                  className="w-full font-semibold"
                  style={{ 
                    backgroundColor: path.color || '#00FF00',
                    color: '#000'
                  }}
                >
                  Start Path
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {paths.length === 0 && !error && (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No learning paths available</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default LearningPaths;
